/**
 * Vocabulary: readers see this feature as "owner reviews"; code, routes, DealHunt paths and
 * analytics events keep the historical "owner note" name on purpose — do not rename them.
 *
 * "Which product do you own?" — the typeahead behind every declaration surface: the category
 * capture band (`OwnCaptureBand`) and the guide chooser (`GuideCapture`) run the same one.
 * Debounced `fetchSuggest` over the browser Meili index, arrow / Enter / Escape keys, and a
 * mousedown outside the box that closes the dropdown.
 *
 * Extracted from `OwnCaptureBand` for the guide funnel (2026-09-15, WP2) — same timings, same
 * keys, same close rule. `select()` is the one way in: it empties the box before handing the
 * hit to the caller, so a click and an Enter behave identically.
 *
 * Two head starts (2026-09-25), so the declared strip's ask is not a 2–3 s wait after the pick:
 * opening the box asks `/me` (`prefetchMe`), and a result the highlight rests on for
 * `PREVIEW_MS` is handed to `onPreview`, which starts its resolve (`prefetchDeclaration`).
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { fetchSuggest } from 'lib/dealhunt/client'
import type { DealHuntSuggestHit } from 'lib/dealhunt/types'
import { prefetchMe } from './own-note'

/** Typing pause before a query goes out, and the shortest query worth sending. */
export const DEBOUNCE_MS = 275
export const MIN_QUERY = 2
/** How long the highlight rests on a result before it counts as "about to pick": a pointer
 *  sweeping down the list costs no lookups (each resolve is ~30 DB queries). */
export const PREVIEW_MS = 150

export interface OwnTypeahead {
  query: string
  setQuery: (value: string) => void
  open: boolean
  setOpen: (value: boolean) => void
  loading: boolean
  results: DealHuntSuggestHit[]
  highlight: number
  setHighlight: (index: number) => void
  /** The query without its edges — what was actually searched. */
  trimmed: string
  /** The dropdown is on screen: open, with enough typed to have searched. */
  showDropdown: boolean
  /** Put this on the element wrapping the input and the dropdown (the outside-click test). */
  rootRef: React.RefObject<HTMLDivElement>
  onKeyDown: (e: React.KeyboardEvent) => void
  /** Take this hit: close, empty the box, hand it over. */
  select: (hit: DealHuntSuggestHit) => void
}

/**
 * `keep` drops hits before they become `results` (the community picker's category scope), so the
 * highlight, Enter and the empty state all walk the same list the reader sees.
 */
export function useOwnTypeahead(
  onPick: (hit: DealHuntSuggestHit) => void,
  onPreview?: (hit: DealHuntSuggestHit) => void,
  keep?: (hit: DealHuntSuggestHit) => boolean
): OwnTypeahead {
  const [query, setQuery] = useState('')
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [results, setResults] = useState<DealHuntSuggestHit[]>([])
  const [highlight, setHighlight] = useState(-1)
  const rootRef = useRef<HTMLDivElement>(null)
  // The caller hands a fresh closure every render; keep `select` stable for effect deps.
  const onPickRef = useRef(onPick)
  onPickRef.current = onPick
  const onPreviewRef = useRef(onPreview)
  onPreviewRef.current = onPreview
  const keepRef = useRef(keep)
  keepRef.current = keep

  const trimmed = query.trim()
  const searchable = trimmed.length >= MIN_QUERY

  useEffect(() => {
    if (!open || !searchable) {
      setResults([])
      setLoading(false)
      return undefined
    }
    const ctrl = new AbortController()
    const timer = setTimeout(() => {
      setLoading(true)
      fetchSuggest(trimmed, ctrl.signal)
        .then(res => {
          const hits = res?.hits ?? []
          const filter = keepRef.current
          setResults(filter ? hits.filter(filter) : hits)
          setHighlight(-1)
        })
        .catch(() => undefined)
        .finally(() => setLoading(false))
    }, DEBOUNCE_MS)
    return () => {
      clearTimeout(timer)
      ctrl.abort()
    }
  }, [open, searchable, trimmed])

  useEffect(() => {
    if (open) {
      prefetchMe()
    }
  }, [open])

  const previewHit = open && highlight >= 0 ? results[highlight] : undefined
  useEffect(() => {
    if (!previewHit) {
      return undefined
    }
    const timer = setTimeout(() => onPreviewRef.current?.(previewHit), PREVIEW_MS)
    return () => clearTimeout(timer)
  }, [previewHit])

  useEffect(() => {
    if (!open) {
      return undefined
    }
    const onClick = (e: MouseEvent): void => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [open])

  const reset = useCallback((): void => {
    setOpen(false)
    setQuery('')
  }, [])

  const select = useCallback(
    (hit: DealHuntSuggestHit): void => {
      reset()
      onPickRef.current(hit)
    },
    [reset]
  )

  const onKeyDown = (e: React.KeyboardEvent): void => {
    if (e.key === 'Escape') {
      setOpen(false)
      return
    }
    if (!results.length) {
      return
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setHighlight(h => Math.min(h + 1, results.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setHighlight(h => Math.max(h - 1, 0))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      const hit = results[highlight >= 0 ? highlight : 0]
      if (hit) {
        select(hit)
      }
    }
  }

  return {
    query,
    setQuery,
    open,
    setOpen,
    loading,
    results,
    highlight,
    setHighlight,
    trimmed,
    showDropdown: open && searchable,
    rootRef,
    onKeyDown,
    select,
  }
}
